import type { TimesheetLineItem } from '@time-tracking/shared';

/**
 * A row in the submit-dialog / celebration hours recap. Either a single project
 * (all its task lines summed) or the one combined indirect row.
 */
export interface RecapRow {
  key: string;
  label: string;
  minutes: number;
  indirect: boolean;
}

export interface Recap {
  /** Project rows (sorted by label) followed by the indirect row when present. */
  rows: RecapRow[];
  projectMinutes: number;
  indirectMinutes: number;
  totalMinutes: number;
  projectCount: number;
}

function lineMinutes(line: TimesheetLineItem, overrides?: ReadonlyMap<string, number>): number {
  const override = overrides?.get(line.lineId);
  if (override !== undefined) return override;
  return line.entries.reduce((sum, e) => sum + e.regularMinutes, 0);
}

/**
 * Builds the hours recap shown at submit time and on the celebration screen:
 * one row per project that has hours, then a single combined "Leave & Non-Project"
 * indirect row for all admin-category time. The rows always sum to the total.
 *
 * Project-task lines carry an `assignmentId`; non-project (admin-category) lines
 * do not — that is the project-vs-indirect distinction the timesheet model uses.
 */
export function computeRecap(
  lines: readonly TimesheetLineItem[],
  overrides?: ReadonlyMap<string, number>,
): Recap {
  const projectByKey = new Map<string, RecapRow>();
  let indirectMinutes = 0;

  for (const line of lines) {
    const minutes = lineMinutes(line, overrides);
    if (minutes === 0) continue;
    if (line.assignmentId !== null) {
      // Fall back to the (unique) lineId, never a shared 'unknown' bucket, so
      // project lines with missing join data aren't incorrectly merged.
      const key = line.projectId ?? line.projectName ?? line.lineId;
      const existing = projectByKey.get(key);
      if (existing) {
        existing.minutes += minutes;
      } else {
        projectByKey.set(key, {
          key,
          label: line.projectName ?? 'Unknown project',
          minutes,
          indirect: false,
        });
      }
    } else {
      indirectMinutes += minutes;
    }
  }

  const rows = [...projectByKey.values()].sort((a, b) => a.label.localeCompare(b.label));
  const projectMinutes = rows.reduce((sum, r) => sum + r.minutes, 0);
  const projectCount = rows.length;

  if (indirectMinutes > 0) {
    rows.push({
      key: '__indirect',
      label: 'Leave & Non-Project',
      minutes: indirectMinutes,
      indirect: true,
    });
  }

  return {
    rows,
    projectMinutes,
    indirectMinutes,
    totalMinutes: projectMinutes + indirectMinutes,
    projectCount,
  };
}
